/**
 * Context for CXS MCP settings (auth IDs, service/dashboard ID).
 *
 * Persists settings in a cookie so they survive page refreshes and are
 * available to the server. Connected tools are populated from stream
 * events (not persisted).
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const COOKIE_NAME = 'cxs_mcp_settings';
const COOKIE_MAX_AGE = 365 * 24 * 60 * 60; // 1 year in seconds

export interface ConnectedTool {
  name: string;
  description: string;
  group: string;
}

export type McpStatus = 'idle' | 'connecting' | 'connected' | 'error';

export interface CxsMcpContextValue {
  sessionToken: string;
  serviceId: string;
  cxsUserId: string;
  cxsOrganizationId: string;
  setSessionToken: (token: string) => void;
  setServiceId: (id: string) => void;
  setCxsUserId: (id: string) => void;
  setCxsOrganizationId: (id: string) => void;
  connectedTools: ConnectedTool[];
  setConnectedTools: (tools: ConnectedTool[]) => void;
  mcpStatus: McpStatus;
  setMcpStatus: (status: McpStatus) => void;
}

const CxsMcpContext = createContext<CxsMcpContextValue | null>(null);

interface StoredSettings {
  sessionToken: string;
  serviceId: string;
  cxsUserId: string;
  cxsOrganizationId: string;
}

/** Read a cookie by name. */
function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

/** Write a cookie with SameSite=Lax and a long max-age. */
function setCookie(name: string, value: string) {
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`;
}

const LEGACY_STORAGE_KEY = 'cxs-mcp-settings';

function loadSettings(): StoredSettings {
  const empty: StoredSettings = { sessionToken: '', serviceId: '', cxsUserId: '', cxsOrganizationId: '' };
  try {
    // Try cookie first
    const raw = getCookie(COOKIE_NAME);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        sessionToken: parsed.sessionToken || '',
        serviceId: parsed.serviceId || '',
        cxsUserId: parsed.cxsUserId || '',
        cxsOrganizationId: parsed.cxsOrganizationId || '',
      };
    }
    // Migrate from legacy localStorage if cookie not set
    const legacy = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (legacy) {
      const parsed = JSON.parse(legacy);
      const settings: StoredSettings = {
        sessionToken: parsed.sessionToken || '',
        serviceId: parsed.serviceId || '',
        cxsUserId: parsed.cxsUserId || '',
        cxsOrganizationId: parsed.cxsOrganizationId || '',
      };
      // Migrate to cookie and clean up localStorage
      setCookie(COOKIE_NAME, JSON.stringify(settings));
      localStorage.removeItem(LEGACY_STORAGE_KEY);
      return settings;
    }
  } catch {
    // ignore parse errors
  }
  return empty;
}

function saveSettings(settings: StoredSettings) {
  setCookie(COOKIE_NAME, JSON.stringify(settings));
}

export function CxsMcpProvider({ children }: { children: React.ReactNode }) {
  const [sessionToken, setSessionTokenState] = useState(() => loadSettings().sessionToken);
  const [serviceId, setServiceIdState] = useState(() => loadSettings().serviceId);
  const [cxsUserId, setCxsUserIdState] = useState(() => loadSettings().cxsUserId);
  const [cxsOrganizationId, setCxsOrganizationIdState] = useState(() => loadSettings().cxsOrganizationId);
  const [connectedTools, setConnectedTools] = useState<ConnectedTool[]>([]);
  const [mcpStatus, setMcpStatus] = useState<McpStatus>('idle');

  const setSessionToken = useCallback((token: string) => {
    setSessionTokenState(token);
  }, []);

  const setServiceId = useCallback((id: string) => {
    setServiceIdState(id);
  }, []);

  const setCxsUserId = useCallback((id: string) => {
    setCxsUserIdState(id);
  }, []);

  const setCxsOrganizationId = useCallback((id: string) => {
    setCxsOrganizationIdState(id);
  }, []);

  // Persist to cookie whenever settings change
  useEffect(() => {
    saveSettings({ sessionToken, serviceId, cxsUserId, cxsOrganizationId });
  }, [sessionToken, serviceId, cxsUserId, cxsOrganizationId]);

  // Expose setConnectedTools for E2E testing
  useEffect(() => {
    (window as any).__cxsMcpSetTools = setConnectedTools;
    return () => { delete (window as any).__cxsMcpSetTools; };
  }, [setConnectedTools]);

  return (
    <CxsMcpContext.Provider
      value={{
        sessionToken,
        serviceId,
        cxsUserId,
        cxsOrganizationId,
        setSessionToken,
        setServiceId,
        setCxsUserId,
        setCxsOrganizationId,
        connectedTools,
        setConnectedTools,
        mcpStatus,
        setMcpStatus,
      }}
    >
      {children}
    </CxsMcpContext.Provider>
  );
}

export function useCxsMcp(): CxsMcpContextValue {
  const ctx = useContext(CxsMcpContext);
  if (!ctx) {
    throw new Error('useCxsMcp must be used within a CxsMcpProvider');
  }
  return ctx;
}

export function useCxsMcpOptional(): CxsMcpContextValue | null {
  return useContext(CxsMcpContext);
}
